import type {
	DokumentyDokumentSeznamDto,
	KosPolozkaKosSeznamDto,
} from '@gov-nx/api';
import { getEntries, is } from '@gov-nx/core/types';
import type { MyFilesContextType } from '../context.types';

interface MyFilesDownloadHookProps {
	formMethods: MyFilesContextType['formDefinition']['formMethods'];
	isSelectedRecycleBin: boolean;
	documents?: DokumentyDokumentSeznamDto;
	documentsBinned?: KosPolozkaKosSeznamDto;
}

const first = <A, B>(array: [A, B]): A => array[0];
const last = <A, B>(array: [A, B]): B => array[1];

const set = <
	T extends { [key: string]: { checked: boolean; souborId?: string } },
>(
	id: number | undefined,
	value: { checked: boolean; souborId?: string },
	obj: T
): T => {
	if (!id) return obj;
	return { ...obj, [id]: value.checked ? value : undefined };
};

export const useMyFilesSelection = ({
	documents,
	isSelectedRecycleBin,
	documentsBinned,
	formMethods,
}: MyFilesDownloadHookProps) => {
	const setValue = (
		isChecked: boolean
	): { [key: string]: { checked: boolean; souborId: string } } => {
		if (isSelectedRecycleBin) {
			return (documentsBinned?.seznam ?? [])?.reduce(
				(all, document) => set(document.id, { checked: isChecked }, all),
				{} as { [key: string]: { checked: boolean; souborId: string } }
			);
		}
		return (documents?.seznam ?? []).reduce(
			(all, document) =>
				set(
					document.dokumentId,
					{ checked: isChecked, souborId: document.soubory?.[0]?.souborId },
					all
				),
			{} as { [key: string]: { checked: boolean; souborId: string } }
		);
	};

	const selected = getEntries(formMethods.watch('selection') ?? {})
		.filter((entry) => last(entry)?.checked)
		.map((entry) =>
			last(entry)
				? {
						documentId: Number(first(entry)),
						souborId: last(entry)?.souborId,
					}
				: undefined
		)
		.filter(is); // filter out unchecked

	const isItemWithoutFileInSelectedIds = (): boolean => {
		const hasMatchingUndefinedSize = selected.find((document) => {
			const matchingObject = documents?.seznam?.find(
				(obj) => obj.dokumentId === document.documentId
			);

			return !!(matchingObject && !is(matchingObject.soubory?.[0]?.velikost));
		});
		return !!hasMatchingUndefinedSize;
	};

	const isItemWithNoFileInSelection = isItemWithoutFileInSelectedIds();

	const isAllSelected = () => {
		if (selected.length === 0) return false;
		return isSelectedRecycleBin
			? selected.length === documentsBinned?.seznam?.length
			: selected.length === documents?.seznam?.length;
	};

	return {
		selected,
		isAllSelected: isAllSelected(),
		isPartialySelected: selected.length > 0 && !isAllSelected(),
		isItemWithNoFileInSelection,
		toggleSelectAll: (isChecked: boolean) =>
			formMethods.setValue('selection', setValue(isChecked)),
		resetSelectedIds: () => formMethods.setValue('selection', setValue(false)),
		onSelectAll: () => formMethods.setValue('selection', setValue(true)),
	};
};
